import React, { useContext, useEffect, useState } from 'react';
import './PlaceOrder.css';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import { useMultiCart } from '../../Context/MultiCartContext';
import { assets } from '../../assets/assets';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const PlaceOrder = () => {
    const { t } = useTranslation();
    const [payment, setPayment] = useState("cod");
    const [orderDetails, setOrderDetails] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [afripayAvailable, setAfripayAvailable] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const { 
        token, 
        url, 
        setCartItems, 
        clearCart,
        currency, 
        updateNotificationCount,
        isTokenLoading
    } = useContext(StoreContext);
    
    const { clearRestaurantCart, fetchUserCarts, clearAllCarts } = useMultiCart();
    
    const navigate = useNavigate();
    const location = useLocation();

    // Load order details from sessionStorage on component mount
    useEffect(() => {
        console.log('PlaceOrder: Loading order details...');
        const savedOrderDetails = sessionStorage.getItem('orderDetails');
        const stateOrderDetails = location.state?.orderDetails;
        
        console.log('PlaceOrder: State orderDetails:', !!stateOrderDetails);
        console.log('PlaceOrder: Saved orderDetails:', !!savedOrderDetails);
        
        if (stateOrderDetails) {
            console.log('PlaceOrder: Using state orderDetails');
            setOrderDetails(stateOrderDetails);
        } else if (savedOrderDetails) {
            try {
                const parsedDetails = JSON.parse(savedOrderDetails);
                console.log('PlaceOrder: Using saved orderDetails:', parsedDetails);
                setOrderDetails(parsedDetails);
            } catch (error) {
                console.error('Failed to parse order details:', error);
                toast.error(t('placeOrder.invalid_order_details'));
                navigate('/order-details');
                setIsLoading(false);
                return;
            }
        } else {
            console.log('PlaceOrder: No order details found, redirecting to order-details');
            toast.error(t('placeOrder.complete_order_details_first'));
            navigate('/order-details');
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    }, [location.state, navigate]);

    // Redirect if not coming from order details or no saved details
    useEffect(() => {
        if (!location.state?.fromOrderDetails && !sessionStorage.getItem('orderDetails')) {
            navigate('/order-details');
        }
    }, [location.state, navigate]);

    // Check if Afripay is available on component mount
    useEffect(() => {
        const checkAfripayAvailability = async () => {
            try {
                // Simple endpoint check to see if Afripay is configured
                const response = await axios.get(`${url}/api/order/check-afripay`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                setAfripayAvailable(response.data.available);
            } catch (error) {
                console.log("Afripay might not be configured:", error);
                setAfripayAvailable(false);
            }
        };
        
        if (token) {
            checkAfripayAvailability();
        }
    }, [token, url]);

    // Helper function to get cart items for display
    const getCartItems = () => {
        if (!orderDetails) return [];
        
        if (orderDetails.restaurantCart) {
            return orderDetails.restaurantCart.items || [];
        } else {
            // For legacy single cart orders, we'd need to reconstruct from stored data
            // This is less common with the new flow
            return [];
        }
    };

    // Helper function to get totals
    const getOrderTotals = () => {
        if (!orderDetails) return { subtotal: 0, deliveryFee: 0, total: 0 };
        
        if (orderDetails.restaurantCart) {
            const subtotal = orderDetails.restaurantCart.subtotal || 0;
            const deliveryFee = orderDetails.deliveryFee || 0;
            return {
                subtotal,
                deliveryFee,
                total: subtotal + deliveryFee
            };
        }
        
        return { subtotal: 0, deliveryFee: 0, total: 0 };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting || !orderDetails) return;
        setIsSubmitting(true);
        
        try {
            const { customerInfo, deliveryType, restaurantId, restaurantCart, deliveryFee } = orderDetails;
            
            // Prepare items array for order
            let items = [];
            
            if (restaurantCart?.items) {
                items = restaurantCart.items.map(item => ({
                    foodId: item.foodId || item._id,
                    food: item.foodId || item._id,
                    name: item.name || "Food Item",
                    price: item.price || 0,
                    quantity: item.quantity,
                    specialInstructions: item.specialInstructions || ""
                }));
            }
            
            if (items.length === 0) {
                toast.error(t('placeOrder.cart_empty'));
                setIsSubmitting(false);
                return;
            }
            
            // Calculate totals from order details
            const subtotal = restaurantCart?.subtotal || 0;
            const tax = 0; // No tax - included in item prices
            const total = subtotal + tax + deliveryFee;
            
            // Format data according to backend validation requirements
            let orderData = {
                restaurantId: restaurantId,
                items: items,
                subtotal: subtotal,
                tax: tax,
                deliveryFee: deliveryFee,
                tip: 0,
                total: total,
                address: {
                    street: customerInfo.street || "Restaurant Pickup",
                    quartier: customerInfo.quartier || "Restaurant Location",
                    city: customerInfo.city || "Bujumbura",
                    state: customerInfo.state || "Bujumbura Mairie",
                    country: customerInfo.country || "Burundi",
                    zipCode: customerInfo.zipcode || "",
                    coordinates: customerInfo.coordinates,
                    deliveryInstructions: deliveryType === 'pickup' ? "Customer pickup at restaurant" : (customerInfo.deliveryInstructions || "")
                },
                contactPhone: customerInfo.phone,
                paymentMethod: payment === "afripay" ? "afripay" : "cash",
                deliveryType: deliveryType
            };
            
            // If Afripay is selected but not available or fails, fallback to COD
            const useAfripay = payment === "afripay" && afripayAvailable;
            
            if (useAfripay) {
                try {
                    // Use the direct-place endpoint for Afripay too
                    let response = await axios.post(url + "/api/order/direct-place", orderData, { 
                        headers: { 
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (response.data.success && response.data.afripay_form_data) {
                        // Redirect to Afripay checkout using the provided form data
                        redirectToAfripay(response.data.afripay_form_data);
                    } else {
                        console.error("Afripay session creation failed:", response.data);
                        
                        // Check for specific error codes
                        if (response.data.error?.status === 402 || 
                            (response.data.error?.message && response.data.error.message.includes("402"))) {
                            
                            // Clear cart and session storage
                            if (orderDetails.restaurantCart) {
                                await clearRestaurantCart(restaurantId);
                            } else {
                                await clearCart();
                            }
                            sessionStorage.removeItem('orderDetails');
                            
                            // Redirect to orders page and scroll to top
                            navigate("/myorders");
                            window.scrollTo(0, 0);
                            return;
                        }
                        
                        // If Afripay session creation fails, fallback to COD
                        if (response.data.error?.includes("Afripay") || response.data.message?.includes("Afripay")) {
                            toast.warning(t('placeOrder.afripay_payment_not_available'));
                            // Update payment method to COD and retry
                            orderData.paymentMethod = "cash";
                            processCashOrder(orderData, restaurantId);
                        } else {
                            toast.error(response.data.message || t('placeOrder.failed_to_create_payment_session'));
                            setIsSubmitting(false);
                        }
                    }
                } catch (afripayError) {
                    console.error("Afripay error:", afripayError);
                    
                    // Check for specific 402 Payment Required error
                    if (afripayError.response?.status === 402 || 
                        (afripayError.message && afripayError.message.includes("402"))) {
                        
                        // Clear cart and session storage
                        if (orderDetails.restaurantCart) {
                            await clearRestaurantCart(restaurantId);
                        } else {
                            await clearCart();
                        }
                        sessionStorage.removeItem('orderDetails');
                        
                        // Redirect to orders page and scroll to top
                        navigate("/myorders");
                        window.scrollTo(0, 0);
                        return;
                    }
                    
                    // Fallback to COD if Afripay fails
                    toast.warning(t('placeOrder.afripay_payment_failed'));
                    orderData.paymentMethod = "cash";
                    processCashOrder(orderData, restaurantId);
                }
            } else {
                // For COD, process the order directly
                processCashOrder(orderData, restaurantId);
            }
        } catch (error) {
            console.error("Error placing order:", error);
            
            if (error.response) {
                console.error("Response data:", error.response.data);
                console.error("Response status:", error.response.status);
                toast.error(error.response.data.message || t('placeOrder.error_processing_your_order'));
            } else if (error.request) {
                console.error("No response received:", error.request);
                toast.error(t('placeOrder.server_not_responding'));
            } else {
                console.error("Error message:", error.message);
                toast.error(t('placeOrder.error_creating_order', { message: error.message }));
            }
            
            setIsSubmitting(false);
        }
    };
    
    // Helper function to process cash orders
    const processCashOrder = async (orderData, restaurantId) => {
        try {
            // Force payment method to cash
            orderData.paymentMethod = "cash";
            
            let response = await axios.post(url + "/api/order/direct-place", orderData, { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
                // Removed withCredentials to avoid CORS issues
                // withCredentials: true
            });
            
            if (response.data.success) {
                // Clear cart and session storage
                console.log('Order placed successfully, clearing cart...');
                console.log('Order details:', orderDetails);
                console.log('Restaurant cart:', orderDetails.restaurantCart);
                console.log('Restaurant ID:', restaurantId);
                
                // Extract the actual restaurant ID string
                const actualRestaurantId = typeof restaurantId === 'object' ? restaurantId._id : restaurantId;
                console.log('Actual restaurant ID:', actualRestaurantId);
                
                try {
                    // Clear both types of carts to ensure nothing is left behind
                    console.log('Clearing all carts...');
                    
                    // Clear restaurant cart if it exists
                    if (orderDetails.restaurantCart) {
                        console.log('Clearing restaurant cart...');
                        await clearRestaurantCart(actualRestaurantId);
                        console.log('Restaurant cart cleared successfully');
                    }
                    
                    // Also clear regular cart as backup
                    console.log('Clearing regular cart as backup...');
                    await clearCart();
                    console.log('Regular cart cleared successfully');
                    
                    console.log('All carts cleared successfully');
                    
                    // Force clear all carts as a backup
                    try {
                        console.log('Force clearing all carts as backup...');
                        await clearAllCarts();
                        console.log('All carts force cleared successfully');
                    } catch (refreshError) {
                        console.error('Error clearing all carts:', refreshError);
                    }
                } catch (error) {
                    console.error('Error clearing cart:', error);
                    // Continue with order success even if cart clearing fails
                }
                
                sessionStorage.removeItem('orderDetails');
                
                // Also clear any cached cart data from localStorage
                localStorage.removeItem('cartItems');
                console.log('LocalStorage cart data cleared');
                
                toast.success(t('placeOrder.order_placed_successfully'));
                
                // Update notification count to reflect new active order
                updateNotificationCount(prev => prev + 1);
                
                // Longer delay to ensure all cart clearing operations complete before navigation
                setTimeout(() => {
                    navigate("/myorders");
                    window.scrollTo(0, 0);
                }, 500);
            } else {
                console.error("Order failed with error:", response.data);
                toast.error(response.data.message || t('placeOrder.failed_to_place_order'));
            }
        } catch (codError) {
            console.error("COD error:", codError.response?.data || codError.message);
            
            if (codError.response?.data?.errors && codError.response.data.errors.length > 0) {
                const errorMessages = codError.response.data.errors
                    .slice(0, 3)
                    .map(err => `${err.field}: ${err.message}`)
                    .join('; ');
                toast.error(t('placeOrder.validation_failed', { errors: errorMessages }));
            } else {
                toast.error(codError.response?.data?.message || t('placeOrder.failed_to_place_order'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to redirect to Afripay
    const redirectToAfripay = (formData) => {
        // Create a form and submit it to Afripay
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://www.afripay.africa/checkout/index.php';
        
        // Add all form fields
        Object.keys(formData).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = formData[key];
            form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
    };

    useEffect(() => {
        if (!isLoading && !isTokenLoading) {
            if (!token) {
                toast.error(t('placeOrder.sign_in_first'));
                navigate('/cart');
            } else if (!orderDetails) {
                // orderDetails not available - should be handled by the loading useEffect above
                // Don't redirect here, let the loading useEffect handle it
                console.log("No orderDetails available");
            }
        }
    }, [token, orderDetails, isLoading, isTokenLoading]);





    // Note: Stripe payment verification is handled by the dedicated /verify route
    // The Stripe checkout will redirect to /verify, not back to this page

    if (isLoading || isTokenLoading || !orderDetails) {
        return (
            <div className="place-order loading">
                <div className="loading-message">{t('placeOrder.loading_order_details')}</div>
            </div>
        );
    }

    const cartItems = getCartItems();
    const totals = getOrderTotals();

    return (
        <form onSubmit={handleSubmit} className='place-order'>
            <div className="place-order-left">
                <h2>{t('placeOrder.review_your_order')}</h2>
                
                {/* Order Summary */}
                <div className="order-summary-section">
                    <h3>{t('placeOrder.order_details')}</h3>
                    <div className="order-type-display">
                        <span className="order-type-icon">
                            {orderDetails.deliveryType === 'delivery' ? '🚚' : '🏪'}
                        </span>
                        <span className="order-type-text">
                            {orderDetails.deliveryType === 'delivery' ? t('placeOrder.delivery_order') : t('placeOrder.pickup_order')}
                        </span>
                    </div>
                    
                    <div className="cart-items-review">
                        <h4>{t('placeOrder.items', { count: cartItems.length })}</h4>
                        {cartItems.map((item, index) => (
                            <div key={index} className="cart-item-review">
                                <div className="item-info">
                                    <span className="item-name">{item.name}</span>
                                    <span className="item-quantity">x{item.quantity}</span>
                                </div>
                                <span className="item-total">{Math.round(item.price * item.quantity)} {currency}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Customer Information Display */}
                <div className="customer-info-display">
                    <h3>{t('placeOrder.customer_information')}</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">{t('placeOrder.name')}:</span>
                            <span className="info-value">{orderDetails.customerInfo.firstName} {orderDetails.customerInfo.lastName}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('placeOrder.email')}:</span>
                            <span className="info-value">{orderDetails.customerInfo.email}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('placeOrder.phone')}:</span>
                            <span className="info-value">{orderDetails.customerInfo.phone}</span>
                        </div>
                    </div>
                </div>

                {/* Delivery Information Display (only for delivery orders) */}
                {orderDetails.deliveryType === 'delivery' && (
                    <div className="delivery-info-display">
                        <h3>{t('placeOrder.delivery_information')}</h3>
                        <div className="delivery-address">
                            <p>{orderDetails.customerInfo.street}</p>
                            <p>{orderDetails.customerInfo.quartier}, {orderDetails.customerInfo.city}</p>
                            <p>{orderDetails.customerInfo.state}, {orderDetails.customerInfo.country}</p>
                            {orderDetails.customerInfo.zipcode && <p>{orderDetails.customerInfo.zipcode}</p>}
                            {orderDetails.customerInfo.deliveryInstructions && (
                                <div className="delivery-instructions">
                                    <strong>{t('placeOrder.instructions')}:</strong> {orderDetails.customerInfo.deliveryInstructions}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Edit Button */}
                <div className="edit-order-section">
                    <button 
                        type="button" 
                        className="edit-order-btn"
                        onClick={() => navigate('/order-details', { 
                            state: { 
                                restaurantCart: orderDetails.restaurantCart,
                                fromCheckout: true 
                            } 
                        })}
                    >
                        {t('placeOrder.edit_order_details')}
                    </button>
                </div>

                {/* Payment Method Selection */}
                <div className="payment-section">
                    <h3>{t('placeOrder.payment_method')}</h3>
                    <div className="payment-options">
                        <div 
                            className={`payment-option ${payment === 'cod' ? 'active' : ''}`}
                            onClick={() => setPayment('cod')}
                        >
                            <input 
                                type="radio" 
                                name="payment" 
                                value="cod" 
                                checked={payment === 'cod'}
                                onChange={() => setPayment('cod')}
                            />
                            <label>{t('placeOrder.cash_on_delivery')}</label>
                        </div>
                        
                        {afripayAvailable && (
                            <div 
                                className={`payment-option ${payment === 'afripay' ? 'active' : ''}`}
                                onClick={() => setPayment('afripay')}
                            >
                                <input 
                                    type="radio" 
                                    name="payment" 
                                    value="afripay" 
                                    checked={payment === 'afripay'}
                                    onChange={() => setPayment('afripay')}
                                />
                                <label>{t('placeOrder.mobile_money_bank_card')}</label>
                            </div>
                        )}

                        <div className="payment-option disabled">
                            <input 
                                type="radio" 
                                name="payment" 
                                value="lumicash" 
                                disabled
                            />
                            <label>{t('placeOrder.lumicash_payment')}</label>
                            <span className="coming-soon-badge">{t('placeOrder.coming_soon')}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="place-order-right">
                <div className="cart-total">
                    <h2>{t('placeOrder.order_summary')}</h2>
                    
                    <div className="totals-summary">
                        <div className="cart-total-details">
                            <p>{t('placeOrder.subtotal')}</p>
                            <p>{Math.round(totals.subtotal)} {currency}</p>
                        </div>

                        <div className="cart-total-details">
                            <p>{orderDetails.deliveryType === 'delivery' ? t('placeOrder.delivery_fee') : t('placeOrder.pickup_fee')}</p>
                            <p>{Math.round(totals.deliveryFee)} {currency}</p>
                        </div>
                        <hr className="total-divider" />
                        <div className="cart-total-details total-row">
                            <b>{t('placeOrder.total')}</b>
                            <b>{Math.round(totals.total)} {currency}</b>
                        </div>
                    </div>

                    <button 
                        type='submit' 
                        className={`place-order-btn ${isSubmitting ? 'submitting' : ''}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('placeOrder.processing') : t('placeOrder.place_order')}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default PlaceOrder;